import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Studio from './Studio';
import PurchasePage from './components/PurchasePage';
import PurchaseSuccessPage from './components/PurchaseSuccessPage';
import ErrorBoundary from './components/ErrorBoundary';
import ForegroundPushToast from './components/ForegroundPushToast';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import AccountSettingsPage from './components/AccountSettingsPage';
import TransactionHistoryPage from './components/TransactionHistoryPage';
import NotFoundPage from './components/NotFoundPage';
import GuidesIndexPage from './components/GuidesIndexPage';
import PricingPage from './components/PricingPage';
import LongTermTimelapseGuidePage from './components/LongTermTimelapseGuidePage';
import TimelapseCalculatorPage from './components/TimelapseCalculatorPage';
import PhotoSequencePage from './components/PhotoSequencePage';
import DeflickerPage from './components/DeflickerPage';
import { MOTIONPACE_MARKETING_ENABLED } from './lib/motionPaceMarketing';

import LrtAlternativePage from './components/LrtAlternativePage';
import BrinnoFlickerGuidePage from './components/BrinnoFlickerGuidePage';
import GoproPhotoModeGuidePage from './components/GoproPhotoModeGuidePage';
import HowLongIsATimelapsePage from './components/HowLongIsATimelapsePage';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import SignInDialog from './components/SignInDialog';
import useCredits from './hooks/useCredits';
import usePageTracking from './hooks/usePageTracking';
import { setAnalyticsUserId, trackEvent } from './analytics';
import { resolveAuthEvent } from './analytics-helpers';
import { doorParams } from './doorAttribution';
import './App.css';

// The MotionPace page is pulled in by a `require` inside a branch on the RAW
// env var, and both halves of that are load-bearing.
//
// THE LITERAL, not the imported MOTIONPACE_MARKETING_ENABLED: CRA's DefinePlugin
// replaces `process.env.REACT_APP_*` with a string at parse time, so webpack
// folds this test to `false` for production and never adds the module to the
// graph at all. Reading the constant through an import would defeat that — the
// route would vanish, but the page's markup, its copy and its CSS would all
// still ship inside main.js for anyone who opened the bundle.
//
// `require`, not `React.lazy`: lazy would also split the page out, but it
// resolves asynchronously, and the prerender harness fails a route whose markup
// React does not adopt cleanly on hydration. A synchronous require keeps the
// snapshot hydratable.
//
// Verified by grepping a production build for the page's copy, its CSS and its
// image assets — see src/__tests__/motionPaceGating.test.js.
const MotionPacePage =
  process.env.REACT_APP_MOTIONPACE_MARKETING === 'true'
    // eslint-disable-next-line global-require
    ? require('./components/MotionPacePage').default
    : null;

// The "What is adaptive pacing?" pillar page (#546) rides the SAME switch, for
// the same reason and by the same mechanism: it is the page that DEFINES the
// technique MotionPace automates, so publishing it while the feature is dark
// would announce the feature. Read the comment above before touching this — the
// raw literal and the `require` are load-bearing here for identical reasons.
const AdaptivePacingPage =
  process.env.REACT_APP_MOTIONPACE_MARKETING === 'true'
    // eslint-disable-next-line global-require
    ? require('./components/AdaptivePacingPage').default
    : null;

// The intent-capture guides that link the pillar page (#547). Same switch,
// same mechanism, same reason as the two above: each explains in detail a
// technique MotionPace automates, so shipping one into a dark production build
// would announce the feature. Read the MotionPacePage comment before touching
// these — the raw `process.env` literal and the `require` are load-bearing here
// for identical reasons.
const VariableSpeedTimelapsePage =
  process.env.REACT_APP_MOTIONPACE_MARKETING === 'true'
    // eslint-disable-next-line global-require
    ? require('./components/VariableSpeedTimelapsePage').default
    : null;

const SkipBoringPartsPage =
  process.env.REACT_APP_MOTIONPACE_MARKETING === 'true'
    // eslint-disable-next-line global-require
    ? require('./components/SkipBoringPartsPage').default
    : null;

// The /features index page (#features-tab): the header "Features" link now
// points here instead of directly to /motionpace. Same switch, same mechanism,
// same reason as the pages above — read the MotionPacePage comment before
// touching this. Announces the motion feature surface, so it must not ship
// while the flag is dark.
const FeaturesPage =
  process.env.REACT_APP_MOTIONPACE_MARKETING === 'true'
    // eslint-disable-next-line global-require
    ? require('./components/FeaturesPage').default
    : null;

// The /motiontrim feature page. Sibling to /motionpace — the two modes of the
// Motion section — so it rides the same flag for the same reason.
const MotionTrimPage =
  process.env.REACT_APP_MOTIONPACE_MARKETING === 'true'
    // eslint-disable-next-line global-require
    ? require('./components/MotionTrimPage').default
    : null;

function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [showSignInDialog, setShowSignInDialog] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAnalyticsUserId(currentUser?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  const { credits, loading: creditsLoading } = useCredits(user);
  const [authError, setAuthError] = useState(null);

  const signIn = () => {
    setShowSignInDialog(true);
  };

  const handleSignInSuccess = useCallback((authResult) => {
    setShowSignInDialog(false);
    setAuthError(null);
    const { name, params } = resolveAuthEvent(authResult);
    // Tag the signup/login step with the door the visitor arrived through so
    // the funnel is unbroken from door_view through to project_created. Not
    // spent here — the door keeps its claim until a project actually exists.
    trackEvent(name, { ...params, ...doorParams() });
  }, []);

  const handleSignInError = useCallback((error) => {
    if (error.code === 'auth/internal-error' || error.message?.includes('BLOCKING_FUNCTION_ERROR')) {
      setShowSignInDialog(false);
      setAuthError("Tweedia is currently under active development and not yet open to the public. Stay tuned!");
    } else if (error.code === 'auth/popup-blocked') {
      setAuthError("Your browser blocked the sign-in popup. Please allow popups for this site and try again.");
    } else if (error.code === 'auth/network-request-failed') {
      setAuthError("We couldn't reach the sign-in service. Please check your connection and try again.");
    } else if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
      console.error("Authentication error:", error);
      setAuthError("We couldn't sign you in. Please try again in a moment.");
    }
  }, []);

  const handleDialogClose = useCallback(() => {
    setShowSignInDialog(false);
  }, []);

  return (
    <Router>
      <PageTracker />
      <ForegroundPushToast user={user} />
      <ErrorBoundary>
        <Routes>
          <Route
            path="/"
            element={
              <ErrorBoundary scope="the landing page">
                <LandingPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/studio/:projectId?/:tab?"
            element={
              <ErrorBoundary scope="the studio">
                <Studio
                  user={user}
                  signIn={signIn}
                  showSignInDialog={showSignInDialog}
                  setShowSignInDialog={setShowSignInDialog}
                  handleDialogClose={handleDialogClose}
                  credits={credits}
                  creditsLoading={creditsLoading}
                />
              </ErrorBoundary>
            }
          />
          <Route
            path="/credits"
            element={
              <ErrorBoundary scope="the credits page">
                <PurchasePage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/purchase/success"
            element={
              <ErrorBoundary scope="your purchase confirmation">
                <PurchaseSuccessPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/terms"
            element={
              <ErrorBoundary scope="the terms of service page">
                <TermsOfService user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/privacy"
            element={
              <ErrorBoundary scope="the privacy policy page">
                <PrivacyPolicy user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/pricing"
            element={
              <ErrorBoundary scope="the pricing page">
                <PricingPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/guides"
            element={
              <ErrorBoundary scope="the guides page">
                <GuidesIndexPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/guides/long-term-timelapse"
            element={
              <ErrorBoundary scope="the timelapse guide">
                <LongTermTimelapseGuidePage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/guides/brinno-timelapse-flicker"
            element={
              <ErrorBoundary scope="the Brinno flicker guide">
                <BrinnoFlickerGuidePage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/guides/gopro-time-lapse-photos-to-video"
            element={
              <ErrorBoundary scope="the GoPro time lapse guide">
                <GoproPhotoModeGuidePage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/guides/how-long-is-a-time-lapse"
            element={
              <ErrorBoundary scope="the time lapse length guide">
                <HowLongIsATimelapsePage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/photo-sequence-to-video"
            element={
              <ErrorBoundary scope="the photo sequence page">
                <PhotoSequencePage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/deflicker-timelapse"
            element={
              <ErrorBoundary scope="the deflicker page">
                <DeflickerPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/lrtimelapse-alternative"
            element={
              <ErrorBoundary scope="the LRTimelapse comparison page">
                <LrtAlternativePage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          {/*
            Absent entirely while the flag is off (production before the
            2026-09-10 launch). Rendering nothing at all — rather than a page
            that explains it is unavailable — is the point: the route must not
            resolve, so an unlisted URL 404s like any other typo instead of
            confirming the feature exists.
          */}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Route
              path="/motionpace"
              element={
                <ErrorBoundary scope="the MotionPace page">
                  <MotionPacePage user={user} signIn={signIn} />
                </ErrorBoundary>
              }
            />
          )}
          {/*
            The pillar page that defines "adaptive pacing" (#546). Gated on the
            same constant as /motionpace above, and declared in its own guard
            rather than sharing one: <Routes> only accepts <Route> children, so
            two routes under one guard would need a fragment for no gain.
          */}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Route
              path="/guides/what-is-adaptive-pacing"
              element={
                <ErrorBoundary scope="the adaptive pacing guide">
                  <AdaptivePacingPage user={user} signIn={signIn} />
                </ErrorBoundary>
              }
            />
          )}
          {/*
            The intent-capture cluster (#547) — three guides that answer queries
            people search today and link the pillar page above. One guard each,
            for the same reason the pillar has its own: <Routes> only accepts
            <Route> children, so sharing a guard would need a fragment for no
            gain.
          */}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Route
              path="/guides/variable-speed-timelapse"
              element={
                <ErrorBoundary scope="the variable speed timelapse guide">
                  <VariableSpeedTimelapsePage user={user} signIn={signIn} />
                </ErrorBoundary>
              }
            />
          )}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Route
              path="/guides/skip-the-boring-parts"
              element={
                <ErrorBoundary scope="the skip-the-boring-parts guide">
                  <SkipBoringPartsPage user={user} signIn={signIn} />
                </ErrorBoundary>
              }
            />
          )}
          {/*
            The /features index — the landing the header "Features" link now
            points to. Three feature cards: MotionPace, MotionTrim, Deflicker.
            Gated on the same constant as /motionpace: both pages announce the
            motion feature surface and must not ship while the flag is dark.
          */}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Route
              path="/features"
              element={
                <ErrorBoundary scope="the features page">
                  <FeaturesPage user={user} signIn={signIn} />
                </ErrorBoundary>
              }
            />
          )}
          {/*
            The /motiontrim feature page — the trim mode of the Motion section,
            sibling to /motionpace. Gated on the same constant for the same reason.
          */}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Route
              path="/motiontrim"
              element={
                <ErrorBoundary scope="the MotionTrim page">
                  <MotionTrimPage user={user} signIn={signIn} />
                </ErrorBoundary>
              }
            />
          )}
          <Route
            path="/tools/timelapse-calculator"
            element={
              <ErrorBoundary scope="the timelapse calculator">
                <TimelapseCalculatorPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/account"
            element={
              <ErrorBoundary scope="your account settings">
                <AccountSettingsPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="/transactions"
            element={
              <ErrorBoundary scope="your transaction history">
                <TransactionHistoryPage user={user} signIn={signIn} />
              </ErrorBoundary>
            }
          />
          <Route
            path="*"
            element={
              <ErrorBoundary scope="the page">
                <NotFoundPage user={user} signIn={signIn} credits={credits} creditsLoading={creditsLoading} />
              </ErrorBoundary>
            }
          />
        </Routes>
      </ErrorBoundary>
      {showSignInDialog && (
        <SignInDialog onSuccess={handleSignInSuccess} onError={handleSignInError} onCancel={handleDialogClose} />
      )}
      {authError && (
        <div className="dialog-overlay">
          <div className="dialog-content">
            <h3>Not Available Yet</h3>
            <p>{authError}</p>
            <div className="dialog-buttons">
              <button onClick={() => setAuthError(null)} className="btn-teal">OK</button>
            </div>
          </div>
        </div>
      )}
    </Router>
  );
}

export default App;